import { useMemo, useRef } from 'react';
import type { Entity } from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  collectExternalRefs,
  parseC4Components,
  ANNOTATION_C4_COMPONENTS,
} from '../lib/c4Components';
import { layoutC4Components } from '../lib/c4Layout';
import { exportFileName } from '../lib/exportSvg';
import { getDeploymentLine, getNodeTypeLine } from '../lib/stack';
import { C4ComponentDiagram } from './C4ComponentDiagram';
import { C4ExportButtons } from './C4ExportButtons';
import { C4ZoomableCanvas } from './C4ZoomableCanvas';

const useStyles = makeStyles(theme => ({
  canvas: {
    marginTop: theme.spacing(1),
  },
  external: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  beta: {
    marginBottom: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(2),
  },
  code: {
    fontFamily: 'monospace',
  },
  exportRow: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

export interface C4ComponentPanelProps {
  entity: Entity | undefined;
  loading?: boolean;
}

export function C4ComponentPanel({ entity, loading }: C4ComponentPanelProps) {
  const classes = useStyles();
  const svgRef = useRef<SVGSVGElement | null>(null);

  const components = useMemo(() => parseC4Components(entity), [entity]);
  const layout = useMemo(() => layoutC4Components(components), [components]);

  if (loading) {
    return (
      <Typography className={classes.empty} variant="body2">
        Carregando o contêiner selecionado…
      </Typography>
    );
  }

  if (!entity) {
    return (
      <Typography
        className={classes.empty}
        variant="body2"
        data-testid="c4-component-no-entity"
      >
        Selecione um contêiner para ver os componentes internos.
      </Typography>
    );
  }

  const title = entity.metadata?.title ?? entity.metadata?.name ?? '';

  if (components.length === 0) {
    return (
      <Box className={classes.empty} data-testid="c4-component-empty">
        <Typography variant="body2" gutterBottom>
          {`${title} ainda não declara componentes internos.`}
        </Typography>
        <Typography variant="body2">
          Declare-os na annotation{' '}
          <span className={classes.code}>{ANNOTATION_C4_COMPONENTS}</span> do
          <span className={classes.code}> catalog-info.yaml</span>, uma linha
          por componente:
        </Typography>
        <Typography variant="body2" className={classes.code}>
          {
            '<nome> | <tecnologia> | <descrição> | <destinos separados por vírgula>'
          }
        </Typography>
      </Box>
    );
  }

  const externalRefs = collectExternalRefs(components);

  return (
    <Box data-testid="c4-component-panel">
      <Typography
        className={classes.beta}
        variant="body2"
        color="textSecondary"
      >
        Visualização em BETA: os componentes vêm da annotation{' '}
        <span className={classes.code}>{ANNOTATION_C4_COMPONENTS}</span>, não do
        catálogo.
      </Typography>

      <C4ZoomableCanvas className={classes.canvas}>
        <C4ComponentDiagram
          svgRef={svgRef}
          layout={layout}
          title={title}
          subtitle={getNodeTypeLine(entity)}
          deployment={getDeploymentLine(entity)}
        />
      </C4ZoomableCanvas>

      {externalRefs.length > 0 && (
        <Box className={classes.external} data-testid="c4-component-external">
          <Typography variant="body2" color="textSecondary">
            Fora da fronteira:
          </Typography>
          {externalRefs.map(ref => (
            <Chip
              key={ref}
              size="small"
              label={<EntityRefLink entityRef={ref} />}
            />
          ))}
        </Box>
      )}

      <Box className={classes.exportRow}>
        <C4ExportButtons
          getSvg={() => svgRef.current}
          fileName={exportFileName('c4-componente-l3', title)}
        />
      </Box>
    </Box>
  );
}
